/**
 * FilesUploadPage — drag-n-drop + список текущих датасетов проекта.
 *
 * Project picker: список берёт из useProjects (query-кэш).
 * Выбор project_id сохраняется в URL (?project=...) и store (localStorage).
 *
 * Резолв currentProjectId (stale id → «Default» → lazy-create) выполняет
 * useProjectBootstrap, смонтированный в Shell. На этой странице — только
 * чтение из store + URL → local state.
 *
 * Phase 1.7 (Files UX Refactor 2026-08-06): full-reload (F5 / deep link) ранее
 * давал белый экран — race между useProjectBootstrap (async) и
 * useProjectDatasets (sync render с projectId=stale). Фикс: пока store не
 * зарезолвил currentProjectId, рендерим Loader2 и НЕ вызываем
 * useProjectDatasets вообще (enabled-guard + early return).
 *
 * Phase 1.3: лимит загрузки берём из FILE_MAX_SIZE_MB (lib/constants.ts),
 * не magic 50. Подставляем в локализованную строку `files.upload.subtitle`.
 *
 * Phase 2.5 (2026-08-08): upload progress через честный XHR onUploadProgress +
 * polling lifecycle статуса (uploaded/parsing/ready/failed). После успешного
 * upload показываем FilesUploadProgress с правильной фазой, чтобы пользователь
 * видел «принято → обрабатывается → готово / ошибка».
 *
 * Phase 2.7 (2026-08-08): + AbortController на upload (cancel HTTP),
 *   + parsing polling timeout (PARSING_TIMEOUT_MS) → фаза 'timeout'
 *     с inline-кнопками Retry / Refresh / Cancel polling.
 */
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Loader2 } from 'lucide-react';

import { FileDropzone } from '@/components/files/FileDropzone';
import { DatasetList } from '@/components/files/DatasetList';
import {
  FilesUploadProgress,
  type UploadPhase,
} from '@/components/files/FilesUploadProgress';
import { useProjectDatasets, useUploadDataset, useDataset } from '@/hooks/useFiles';
import { useActiveDataset } from '@/hooks/useActiveDataset';
import { useProjectStore } from '@/store/projectStore';
import { useProjects } from '@/hooks/useProjects';
import { FILE_MAX_SIZE_MB, PARSING_TIMEOUT_MS } from '@/lib/constants';

export function FilesUploadPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const projectIdFromUrl = searchParams.get('project');
  const storeProjectId = useProjectStore((s) => s.currentProjectId);
  const setStoreProjectId = useProjectStore((s) => s.setCurrent);
  const [projectId, setProjectId] = useState<string | null>(projectIdFromUrl ?? storeProjectId);

  // Phase 2.5: state для честного progress + lifecycle polling (ТЗ §1, §3).
  // Phase 2.7: + 'cancelled' (client-cancel HTTP), + 'timeout' (polling
  //   parsing превысил PARSING_TIMEOUT_MS). 'cancelled' (после 'uploading')
  //   — UI нейтральный, polling нет. 'timeout' (после 'parsing') — UI
  //   показывает Retry/Refresh; useDataset продолжает крутиться (на Refresh
  //   user сбрасывает таймер).
  const [pendingFile, setPendingFile] = useState<{
    fileName: string;
    totalBytes: number;
    loadedBytes: number;
    startedAt: number;
    parsingStartedAt: number | null;
    phase: UploadPhase;
    datasetId: string | null;
    errorMessage: string | null;
  } | null>(null);

  // Bootstrap ещё работает: useProjects.isLoading === true И store пуст.
  // В этом случае НЕ вызываем useProjectDatasets (его enabled-guard не
  // поможет — guard стоит на `Boolean(projectId)`, а projectId может быть
  // заполнен из URL-параметра, который stale).
  const { data: projectsData, isLoading: projectsLoading } = useProjects();
  const projects = projectsData?.items ?? [];
  const bootstrapReady = !projectsLoading && !!storeProjectId;

  // Phase 2.5: polling lifecycle (useDataset) ТОЛЬКО когда есть datasetId.
  // useDataset сам выключает polling на ready/failed — контракт.
  const { data: lifecycleDataset } = useDataset(pendingFile?.datasetId ?? null);

  // Phase 2.7: AbortController для upload. Создаём при выборе файла,
  // передаём в upload mutation. Parent (handleCancelUpload) зовёт abort().
  const abortRef = useRef<AbortController | null>(null);

  // Map статус lifecycle → фазу UI (ТЗ §2, §3).
  // Phase 2.7: расширено — отдельный таймер на фазу 'parsing' для решения
  // timeout (PARSING_TIMEOUT_MS). Если polling не приводит к terminal —
  // выставляем phase='timeout' (useDataset продолжает работать; на Refresh
  // мы должны увидеть свежий статус).
  useEffect(() => {
    if (!pendingFile || !lifecycleDataset) return;
    if (pendingFile.phase === 'uploading' || pendingFile.phase === 'uploaded') {
      // На стадии upload polling не нужен — ставим фазу по onSuccess в onFile.
      return;
    }
    if (lifecycleDataset.status === 'parsing') {
      setPendingFile((p) =>
        p
          ? {
              ...p,
              phase: 'parsing' as UploadPhase,
              parsingStartedAt: p.parsingStartedAt ?? Date.now(),
            }
          : p,
      );
    } else if (lifecycleDataset.status === 'ready') {
      setPendingFile((p) =>
        p ? { ...p, phase: 'ready' as UploadPhase, errorMessage: null } : p,
      );
    } else if (lifecycleDataset.status === 'failed') {
      setPendingFile((p) =>
        p
          ? {
              ...p,
              phase: 'failed' as UploadPhase,
              errorMessage: lifecycleDataset.error_message,
            }
          : p,
      );
    } else if (lifecycleDataset.status === 'uploaded') {
      // 'uploaded' тоже считаем стартом parsing-фазы для таймера.
      setPendingFile((p) =>
        p
          ? {
              ...p,
              phase: 'parsing' as UploadPhase,
              parsingStartedAt: p.parsingStartedAt ?? Date.now(),
            }
          : p,
      );
    }
  }, [lifecycleDataset, pendingFile]);

  // Phase 2.7: parsing polling timeout. Если parsingStartedAt зафиксирован
  // и прошло PARSING_TIMEOUT_MS — выставляем phase='timeout'. Polling
  // (useDataset) НЕ останавливаем: на Refresh мы должны получить свежий
  // статус. Если бэк уже перевёл в ready/failed — useEffect выше сам
  // снимет timeout.
  useEffect(() => {
    if (!pendingFile) return;
    if (pendingFile.phase !== 'parsing') return;
    const startedAt = pendingFile.parsingStartedAt ?? pendingFile.startedAt;
    const elapsed = Date.now() - startedAt;
    if (elapsed >= PARSING_TIMEOUT_MS) {
      setPendingFile((p) =>
        p ? { ...p, phase: 'timeout' as UploadPhase } : p,
      );
      return;
    }
    const id = window.setTimeout(() => {
      setPendingFile((p) =>
        p && p.phase === 'parsing'
          ? { ...p, phase: 'timeout' as UploadPhase }
          : p,
      );
    }, PARSING_TIMEOUT_MS - elapsed);
    return () => window.clearTimeout(id);
  }, [pendingFile?.phase, pendingFile?.parsingStartedAt, pendingFile?.startedAt, pendingFile]);

  const uploadMutation = useUploadDataset({
    onUploadProgress: (progressEvent) => {
      const loaded = progressEvent.loaded ?? 0;
      const total = progressEvent.total ?? pendingFile?.totalBytes ?? 0;
      setPendingFile((p) =>
        p ? { ...p, loadedBytes: loaded, totalBytes: total || p.totalBytes } : p,
      );
    },
  });
  // Запрос уходит ТОЛЬКО когда bootstrap отрезолвил currentProjectId — это
  // закрывает full-reload white screen (см. docblock выше).
  const { data, isLoading } = useProjectDatasets(bootstrapReady ? projectId : null);

  // Phase 2.1: подтянуть project.active_dataset_id с бэка → store → DatasetList
  // бейдж «В работе» подсветится после F5 без потери.
  useActiveDataset(bootstrapReady ? projectId : null);

  // === Синхронизация URL ↔ store ===
  // Правила (после регрессии 2026-07-26 / ProjectPicker switch+create):
  //  1. URL ?project= — источник ТОЛЬКО на вход (deep link, browser back/forward).
  //     После mount store рулит. Если они расходятся — URL подтягиваем к store.
  //  2. Если URL протух (валидный uuid, но не в /projects list) — bootstrap
  //     в Shell уже сбросил store; подменяем URL на актуальный id из store.
  //  3. setCurrent в Picker идёт в store + URL синхронно (см. ProjectPicker),
  //     поэтому гонки «store → setCurrent → URL stale» не возникают.
  useEffect(() => {
    // Приоритет: store (он уже отвалидирован bootstrap'ом).
    if (storeProjectId && storeProjectId !== projectId) {
      setProjectId(storeProjectId);
      if (projectIdFromUrl !== storeProjectId) {
        setSearchParams({ project: storeProjectId }, { replace: true });
      }
      return;
    }
    // Store пуст — но URL что-то говорит. Bootstrap мог ещё не отработать;
    // ждём. Не затираем currentId в store, чтобы не потерять валидный выбор.
    if (!storeProjectId && projectIdFromUrl) {
      setProjectId(projectIdFromUrl);
    }
  }, [projectIdFromUrl, storeProjectId, projectId, setSearchParams]);

  const onFile = (file: File) => {
    if (!projectId) return;
    const startedAt = Date.now();
    setPendingFile({
      fileName: file.name,
      totalBytes: file.size,
      loadedBytes: 0,
      startedAt,
      parsingStartedAt: null,
      phase: 'uploading',
      datasetId: null,
      errorMessage: null,
    });
    // Phase 2.7: AbortController создаётся на каждый upload. После abort
    // axios бросит CanceledError — onError ловит и переводит в 'cancelled'.
    const controller = new AbortController();
    abortRef.current = controller;
    uploadMutation.mutate(
      { projectId, file, signal: controller.signal },
      {
        onSuccess: (data) => {
          // Снимаем controller — он больше не нужен, axios закрыл XHR.
          abortRef.current = null;
          setPendingFile((p) =>
            p
              ? {
                  ...p,
                  phase:
                    data.status === 'parsing'
                      ? ('parsing' as UploadPhase)
                      : ('uploaded' as UploadPhase),
                  datasetId: data.dataset_id,
                  loadedBytes: p.totalBytes,
                  parsingStartedAt: Date.now(),
                }
              : p,
          );
        },
        onError: (e: unknown) => {
          abortRef.current = null;
          // Phase 2.7: распознаём AbortError — axios 1.7 ставит
          // name === 'CanceledError', code === 'ERR_CANCELED'.
          const isCanceled =
            (e as { name?: string })?.name === 'CanceledError' ||
            (e as { code?: string })?.code === 'ERR_CANCELED';
          if (isCanceled) {
            setPendingFile((p) =>
              p
                ? { ...p, phase: 'cancelled' as UploadPhase, errorMessage: null }
                : p,
            );
            return;
          }
          // P1 (2026-08-11): различаем «нет ответа» (сеть/сервер недоступен/
          // axios timeout) и «ответ бэка». Backend 4xx/5xx приходит в unified
          // формате { error: { message } } (main.py exception_handler) —
          // показываем его. Без response — локализованное network-сообщение
          // вместо generic «Ошибка», чтобы причина была понятна пользователю.
          const err = e as {
            response?: { data?: { error?: { message?: string } } };
          };
          const msg = err?.response
            ? (err.response.data?.error?.message ?? t('common.error'))
            : t('files.upload.errors.network', {
                defaultValue: 'Не удалось подключиться к серверу',
              });
          setPendingFile((p) =>
            p
              ? { ...p, phase: 'failed' as UploadPhase, errorMessage: msg }
              : p,
          );
        },
      },
    );
  };

  // Phase 2.7: отмена активной передачи файла. Идемпотентно — повторный
  // вызов abort() на закрытом controller безопасный. Если уже не uploading —
  // ничего не делаем. ВАЖНО: server-side cancel не гарантирован. Сервер
  // мог уже принять байты и создать Dataset — он будет виден в DatasetList.
  const handleCancelUpload = () => {
    if (!pendingFile || pendingFile.phase !== 'uploading') return;
    abortRef.current?.abort();
  };

  // Phase 2.7: refresh status при timeout — выставляем phase='parsing'
  // с новым parsingStartedAt. useDataset продолжает polling, и если бэк
  // уже готов/failed — useEffect сверху переключит фазу.
  const handleRefreshStatus = () => {
    if (!pendingFile?.datasetId) return;
    setPendingFile((p) =>
      p
        ? {
            ...p,
            phase: 'parsing' as UploadPhase,
            parsingStartedAt: Date.now(),
            errorMessage: null,
          }
        : p,
    );
  };

  const switchProject = (id: string) => {
    setProjectId(id);
    setStoreProjectId(id);
    setSearchParams({ project: id });
  };

  // При смене проекта — очищаем pendingFile, чтобы не висел старый progress (ТЗ §3).
  const lastProjectRef = useRef(projectId);
  useEffect(() => {
    if (lastProjectRef.current !== projectId) {
      lastProjectRef.current = projectId;
      setPendingFile(null);
      // На смене проекта — тоже сбрасываем abort (на всякий случай).
      abortRef.current?.abort();
      abortRef.current = null;
    }
  }, [projectId]);

  // При unmount страницы — отменим активный upload, чтобы при уходе
  // со страницы XHR не висел в фоне.
  useEffect(() => {
    return () => {
      abortRef.current?.abort();
      abortRef.current = null;
    };
  }, []);

  // Retry — сбрасываем pendingFile. Если был активный upload — отменяем.
  // Пользователь может перейти в DatasetList и нажать Reparse
  // (Phase 1.6 / 2.5 retry semantics = POST /reparse).
  const handleRetry = () => {
    setPendingFile(null);
    abortRef.current?.abort();
    abortRef.current = null;
  };

  // === Full-reload фикс: до готовности bootstrap — пустой Loader, ===
  // === никакого useProjectDatasets и никакого white screen. ===
  if (!bootstrapReady) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[40vh] gap-3">
        <Loader2 className="w-8 h-8 animate-spin text-primary-400" />
        <p className="text-sm text-surface-400">
          {t('files.upload.bootstrap_loading', { defaultValue: 'Загружаем проект…' })}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold mb-1">
            {t('files.upload.title')}
          </h1>
          <p className="text-sm text-surface-400">
            {t('files.upload.subtitle', { mb: FILE_MAX_SIZE_MB })}
          </p>
        </div>
        {projects.length > 1 && (
          <div className="flex items-center gap-2">
            <span className="text-xs text-surface-500">Project:</span>
            <select
              className="bg-surface-800 text-sm rounded px-2 py-1 border border-surface-700"
              value={projectId ?? ''}
              onChange={(e) => switchProject(e.target.value)}
              disabled={!projectId}
            >
              {projects.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>

      <FileDropzone
        onFile={onFile}
        maxMb={FILE_MAX_SIZE_MB}
        disabled={
          !projectId ||
          (uploadMutation.isPending && pendingFile?.phase === 'uploading')
        }
      />

      {pendingFile && (
        <FilesUploadProgress
          fileName={pendingFile.fileName}
          totalBytes={pendingFile.totalBytes}
          loadedBytes={pendingFile.loadedBytes}
          phase={pendingFile.phase}
          startedAt={pendingFile.startedAt}
          parsingStartedAt={pendingFile.parsingStartedAt}
          errorMessage={pendingFile.errorMessage}
          datasetId={pendingFile.datasetId}
          onCancel={handleCancelUpload}
          onOpenTransform={(datasetId) =>
            navigate(`/files/transform?id=${datasetId}`)
          }
          onRetry={handleRetry}
          onRefreshStatus={handleRefreshStatus}
        />
      )}

      <div>
        <h2 className="text-lg font-semibold mb-3">
          {t('files.list.title')}
        </h2>
        <DatasetList
          datasets={data?.items ?? []}
          isLoading={isLoading}
        />
      </div>
    </div>
  );
}
